import React, { useEffect, useMemo, useState } from 'react';
import { post_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import { target_package_plan } from '../../../../constants/index';

const WEEKS_OPTIONS = [
  { value: 12, label: '12 weeks (3-month)' },
  { value: 24, label: '24 weeks (6-month)' },
  { value: 28, label: '28 weeks (7-month)' },
  { value: 52, label: '52 weeks (12-month)' }
];

const PLANS = target_package_plan as { value: string; label: string }[];

/** Order in `target_package_plan` = 1-on-1 tier (higher index = more sessions per week; index 0 is group). */
function planTierIndex(packageUuid: string | undefined): number {
  if (!packageUuid) return -1;
  return PLANS.findIndex((p) => p.value === packageUuid);
}

/** New tier is a downgrade: fewer 1-on-1 sessions (lower index in the list) than the member has now. */
function isTierDowngrade(currentUuid: string | undefined, newUuid: string | undefined): boolean {
  if (!currentUuid || !newUuid || currentUuid === newUuid) return false;
  const cur = planTierIndex(currentUuid);
  const next = planTierIndex(newUuid);
  if (cur < 0 || next < 0) return false;
  return next < cur;
}

/** One line: "PLAN / Monthly|Prepaid" and optional " / N weeks" from `no_of_weeks_originally_enrolled_to`. */
function formatCurrentPlanDisplayLine(
  planName: string | undefined,
  isPrepaid: number | undefined,
  noOfWeeks: number | undefined
): string {
  const name = (planName || '—').trim() || '—';
  const billing = isPrepaid === 1 ? 'Prepaid' : 'Monthly';
  const parts: string[] = [name, billing];
  if (noOfWeeks != null && noOfWeeks > 0) {
    parts.push(`${noOfWeeks} week${noOfWeeks === 1 ? '' : 's'}`);
  }
  return parts.join(' / ');
}

/** POST /cms/api/flexible_charge_subscription — `res.data` (axios) */
interface FlexibleChargeSuccessData {
  success: boolean;
  data?: {
    /** Sequelize row from create_transaction */
    transaction?: { id?: number; order_id?: string; invoice_id?: string };
    purchase_date?: string;
  };
  message?: string;
}

interface Props {
  onClose: () => void;
  set_notification_variable: (show: boolean, type: string, message: string) => void;
  loadStudentData: () => void;
  student_uuid: string;
  studentPlanUuid?: string;
  studentName?: string;
  currentPlan?: string;
  isPrepaid?: number;
  noOfWeeks?: number;
  noOfCycleRemaining?: number;
}

function FlexibleChargeSubscription({
  onClose,
  set_notification_variable,
  loadStudentData,
  student_uuid,
  studentPlanUuid,
  studentName,
  currentPlan,
  isPrepaid,
  noOfWeeks,
  noOfCycleRemaining
}: Props) {
  const [step, setStep] = useState<1 | 2>(1);
  const [upgradePackageUuid, setUpgradePackageUuid] = useState('');
  const [newNoOfWeeks, setNewNoOfWeeks] = useState<number | ''>('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [updatePackagePrice, setUpdatePackagePrice] = useState(false);
  // Optional subscription_start_date — re-enable when product wants it again.
  // const [subscriptionStartDate, setSubscriptionStartDate] = useState('');
  const [startFromOldPrepaid, setStartFromOldPrepaid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingDiscount, setFetchingDiscount] = useState(false);
  const [standardPrice, setStandardPrice] = useState<string | null>(null);
  const [error, setError] = useState('');

  const selectedPackageLabel = upgradePackageUuid
    ? ((target_package_plan as { value: string; label: string }[]).find(
        (p) => p.value === upgradePackageUuid
      )?.label ?? null)
    : null;

  /** Human label for the student's current package (for prepaid-only or fallback). */
  const currentPackageLabel =
    (studentPlanUuid
      ? (target_package_plan as { value: string; label: string }[]).find(
          (p) => p.value === studentPlanUuid
        )?.label
      : undefined) ||
    (currentPlan && currentPlan.trim()) ||
    '—';

  /** Shown in the green "New package" card: new tier if switching, else current name when only converting to prepaid. */
  const newPackagePrimaryLine: string = upgradePackageUuid
    ? selectedPackageLabel || currentPackageLabel
    : newNoOfWeeks
      ? currentPackageLabel
      : '';

  const showNewPackagePlanCard = Boolean(upgradePackageUuid || newNoOfWeeks);

  const selectedWeeksLabel = newNoOfWeeks
    ? (WEEKS_OPTIONS.find((o) => o.value === newNoOfWeeks)?.label ?? null)
    : null;

  /** Package applied to the charge: explicit switch, else current plan (API requires a UUID). */
  const effectivePackageUuid = (upgradePackageUuid || studentPlanUuid || '').trim();

  /** Current prepaid length (or 4 for monthly) — must align with backend `prepaid_members[0].no_of_weeks` or 4. */
  const currentTermWeeks = noOfWeeks != null && noOfWeeks > 0 ? noOfWeeks : 4;
  const downgradeAllowedByPrepay = Boolean(newNoOfWeeks && newNoOfWeeks > currentTermWeeks);

  const switchPackageOptions = useMemo(() => {
    if (!studentPlanUuid) return PLANS;
    const cur = planTierIndex(studentPlanUuid);
    if (cur < 0) return PLANS;
    return PLANS.filter((opt) => {
      const next = planTierIndex(opt.value);
      if (next < 0) return true;
      if (next >= cur) return true;
      return downgradeAllowedByPrepay;
    });
  }, [studentPlanUuid, downgradeAllowedByPrepay]);

  useEffect(() => {
    if (upgradePackageUuid && !switchPackageOptions.some((o) => o.value === upgradePackageUuid)) {
      setUpgradePackageUuid('');
      setStandardPrice(null);
    }
  }, [upgradePackageUuid, switchPackageOptions]);

  const currentPrepaidWeeks = noOfWeeks ?? 0;
  const carryoverApplies =
    startFromOldPrepaid && isPrepaid === 1 && !!newNoOfWeeks && newNoOfWeeks > currentPrepaidWeeks;

  /** Mirrors backend: carryover only when new term is longer; else default ((w/4)-1). */
  const computedCycles = (): number | null => {
    if (!newNoOfWeeks) return null;
    if (carryoverApplies) {
      return (noOfCycleRemaining ?? 0) + (newNoOfWeeks - currentPrepaidWeeks) / 4 - 1;
    }
    return newNoOfWeeks / 4 - 1;
  };

  const handlePreview = async () => {
    if (!amount || Number(amount) <= 0) {
      setError('Amount must be a positive number.');
      return;
    }
    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }
    if (!effectivePackageUuid) {
      setError(
        'Package is required. Use “Keep current package” or pick a package — student plan UUID is missing.'
      );
      return;
    }
    if (isTierDowngrade(studentPlanUuid, effectivePackageUuid) && !downgradeAllowedByPrepay) {
      setError(
        'A lower 1-on-1 tier is only allowed with a longer prepaid than the member has now. Choose a longer prepaid above, or keep/raise the package tier.'
      );
      return;
    }
    setError('');
    setStandardPrice(null);

    const packageUuidForDiscount = effectivePackageUuid;
    // use selected weeks, else fall back to student's current cycle, else 4 (monthly)
    const weeksForDiscount = newNoOfWeeks || noOfWeeks || 4;

    setFetchingDiscount(true);
    try {
      const response = await post_api(
        get_api_url('payment/discount_for_package'),
        {
          student_uuid,
          upgrade_package_uuid: packageUuidForDiscount,
          number_of_weeks: weeksForDiscount,
          utm_medium: 'desktop_web',
          app_platform: 'web_app',
          device_type: 'desktop'
        },
        true
      );
      const total = response?.data?.plan_selected?.total_payable;
      if (total) setStandardPrice(total);
    } catch {
      // non-fatal — preview still opens
    } finally {
      setFetchingDiscount(false);
    }

    setStep(2);
  };

  const handleConfirm = async () => {
    if (!effectivePackageUuid) {
      setError(
        'Package is required. Use “Keep current package” or pick a package — student plan UUID is missing.'
      );
      set_notification_variable(true, 'error', 'Package UUID is required for this charge.');
      return;
    }
    if (isTierDowngrade(studentPlanUuid, effectivePackageUuid) && !downgradeAllowedByPrepay) {
      setError(
        'A lower 1-on-1 tier is only allowed with a longer prepaid than the member has now. Choose a longer prepaid, or keep/raise the package tier.'
      );
      set_notification_variable(true, 'error', 'Invalid tier change for this charge.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {
        student_uuid,
        amount: Number(amount),
        reason: reason.trim(),
        update_package_price: updatePackagePrice,
        new_package_uuid: effectivePackageUuid
      };
      if (newNoOfWeeks) payload.new_no_of_weeks = newNoOfWeeks;
      if (startFromOldPrepaid && newNoOfWeeks) payload.start_from_old_prepaid = true;
      // const ssd = subscriptionStartDate.trim();
      // if (ssd) payload.subscription_start_date = ssd;

      const res = await post_api(get_api_url('flexible_charge_subscription'), payload, true);
      const body = res?.data as FlexibleChargeSuccessData | undefined;
      if (body && body.success === false) {
        throw new Error(body.message || 'Charge failed.');
      }
      const tx = body?.data?.transaction;
      let successMsg = 'Flexible charge applied successfully.';
      if (tx?.id != null) {
        successMsg = `Flexible charge applied. Transaction #${tx.id}.`;
      } else if (tx?.order_id) {
        successMsg = `Flexible charge applied. Order ${tx.order_id}.`;
      }
      set_notification_variable(true, 'success', successMsg);
      loadStudentData();
      onClose();
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { message?: string; success?: boolean } };
        message?: string;
      };
      const data = err?.response?.data;
      const msg = data?.message || err?.message || 'Charge failed.';
      setError(msg);
      set_notification_variable(true, 'error', msg);
    } finally {
      setLoading(false);
    }
  };

  const cycles = computedCycles();
  const currentPlanLine = formatCurrentPlanDisplayLine(currentPlan, isPrepaid, noOfWeeks);

  const fieldLabel = 'block text-sm font-medium text-gray-800 mb-1.5';
  const inputClass =
    'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/15';
  /** One summary row: label (grey) + value, aligned in two columns on sm+ */
  const reviewRow =
    'sm:grid sm:grid-cols-[8.5rem_minmax(0,1fr)] sm:gap-x-4 sm:items-start sm:gap-y-0';
  const reviewDt = 'text-sm text-gray-500 mb-0.5 sm:mb-0 sm:pt-0.5';
  const reviewVal =
    'text-sm min-w-0 sm:text-right text-gray-900 font-medium [word-break:break-word]';

  return (
    <div className="w-full text-gray-900">
      <nav className="mb-6 w-full" aria-label="Charge steps">
        <ol className="m-0 flex w-full list-none gap-1.5 rounded-lg border border-gray-200 bg-gray-100/90 p-1.5 sm:gap-2">
          <li
            className={`flex min-h-[3.25rem] min-w-0 flex-1 flex-col items-center justify-center rounded-md px-2 py-1.5 text-center transition sm:flex-row sm:gap-2 sm:px-2.5 ${
              step === 1
                ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200/90'
                : 'text-gray-500'
            }`}
            aria-current={step === 1 ? 'step' : undefined}
          >
            <span
              className={`mb-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold sm:mb-0 ${
                step === 1 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              1
            </span>
            <span className="min-w-0">
              <span
                className={`block text-xs font-semibold leading-tight ${
                  step === 1 ? 'text-gray-900' : 'text-gray-600'
                }`}
                style={{ textTransform: 'none' }}
              >
                Details
              </span>
              <span
                className="mt-0.5 block text-[10px] font-normal text-gray-500 sm:text-xs"
                style={{ textTransform: 'none' }}
              >
                Plan and amount
              </span>
            </span>
          </li>
          <li
            className={`flex min-h-[3.25rem] min-w-0 flex-1 flex-col items-center justify-center rounded-md px-2 py-1.5 text-center transition sm:flex-row sm:gap-2 sm:px-2.5 ${
              step === 2
                ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200/90'
                : 'text-gray-500'
            }`}
            aria-current={step === 2 ? 'step' : undefined}
          >
            <span
              className={`mb-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold sm:mb-0 ${
                step === 2 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              2
            </span>
            <span className="min-w-0">
              <span
                className={`block text-xs font-semibold leading-tight ${
                  step === 2 ? 'text-gray-900' : 'text-gray-600'
                }`}
                style={{ textTransform: 'none' }}
              >
                Review
              </span>
              <span
                className="mt-0.5 block text-[10px] font-normal text-gray-500 sm:text-xs"
                style={{ textTransform: 'none' }}
              >
                Confirm amount
              </span>
            </span>
          </li>
        </ol>
      </nav>

      {step === 1 ? (
        <>
          <div className="space-y-6">
            <section>
              <h3
                className="text-sm font-semibold text-gray-900 mb-3"
                style={{ textTransform: 'none' }}
              >
                Membership
              </h3>

              <div className="rounded-xl border border-gray-200 bg-white px-3.5 py-3 shadow-sm">
                <p className="text-xs text-gray-500 mb-1" style={{ textTransform: 'none' }}>
                  Now (before this charge)
                </p>
                <p
                  className="text-sm font-semibold text-gray-900 leading-snug"
                  style={{ textTransform: 'none' }}
                >
                  {currentPlanLine}
                </p>
                {isPrepaid === 1 && noOfCycleRemaining != null ? (
                  <p className="mt-1.5 text-xs text-gray-500" style={{ textTransform: 'none' }}>
                    {noOfCycleRemaining} cycle{noOfCycleRemaining === 1 ? '' : 's'} left on this
                    term
                  </p>
                ) : null}
              </div>

              <div className="mt-4 space-y-3.5">
                <div>
                  <label
                    className={fieldLabel}
                    style={{ textTransform: 'none' }}
                    htmlFor="fcs-switch-pkg"
                  >
                    1. Switch to a different package
                  </label>
                  <p className="text-xs text-gray-500 mb-1.5" style={{ textTransform: 'none' }}>
                    Lower 1-on-1 tiers are hidden until you set a <strong>longer</strong> prepaid
                    than their current term (step 2). Skip to keep the same tier.
                  </p>
                  <div id="fcs-switch-pkg">
                    {/*
                      noLabel: empty label would render as ":" in CustomSingleSelect
                      defaultOption: component injects the empty <option> — do not add value:"" in options
                    */}
                    <CustomSingleSelect
                      noLabel
                      defaultOption="Keep current package"
                      options={switchPackageOptions}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setUpgradePackageUuid(e.target?.value || '');
                        setStandardPrice(null);
                      }}
                      name="upgrade_package_uuid"
                      value={upgradePackageUuid}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
                <div>
                  <label
                    className={fieldLabel}
                    style={{ textTransform: 'none' }}
                    htmlFor="fcs-weeks"
                  >
                    2. Prepaid term (optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-1.5" style={{ textTransform: 'none' }}>
                    Pick a 12- / 24- / 28- / 52-week term, or leave as monthly.
                  </p>
                  <select
                    id="fcs-weeks"
                    value={newNoOfWeeks}
                    onChange={(e) => {
                      const v = e.target.value ? Number(e.target.value) : '';
                      setNewNoOfWeeks(v);
                      if (!v) setStartFromOldPrepaid(false);
                      setStandardPrice(null);
                    }}
                    className={inputClass}
                  >
                    <option value="">No change (stay on current term)</option>
                    {WEEKS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                {isPrepaid === 1 && newNoOfWeeks ? (
                  <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-3">
                    <label
                      className="flex items-start gap-2.5 cursor-pointer"
                      htmlFor="fcs-carryover"
                    >
                      <input
                        id="fcs-carryover"
                        type="checkbox"
                        checked={startFromOldPrepaid}
                        onChange={(e) => setStartFromOldPrepaid(e.target.checked)}
                        className="h-4 w-4 mt-0.5 accent-emerald-600 flex-shrink-0"
                      />
                      <span
                        className="text-xs text-gray-600 leading-relaxed"
                        style={{ textTransform: 'none' }}
                      >
                        Extend with cycle carryover when the new term is <strong>longer</strong>{' '}
                        than the current one (prorated custom price). Otherwise the server uses the
                        default cycle count.
                      </span>
                    </label>
                  </div>
                ) : null}
              </div>

              <div
                className={`mt-4 rounded-xl border px-3.5 py-3 shadow-sm ${
                  showNewPackagePlanCard
                    ? 'border-emerald-200/90 bg-gradient-to-b from-white to-emerald-50/50 border-l-[3px] border-l-emerald-500'
                    : 'border-dashed border-gray-200 bg-gray-50/60'
                }`}
              >
                <p className="text-xs text-gray-500 mb-1" style={{ textTransform: 'none' }}>
                  After this charge (preview)
                </p>
                {showNewPackagePlanCard ? (
                  <>
                    <p
                      className="text-sm font-semibold text-gray-900"
                      style={{ textTransform: 'none' }}
                    >
                      {newPackagePrimaryLine || '—'}
                    </p>
                    {selectedWeeksLabel ? (
                      <p className="mt-1.5 text-sm text-gray-700" style={{ textTransform: 'none' }}>
                        {selectedWeeksLabel}
                      </p>
                    ) : (
                      <p className="mt-0.5 text-sm text-gray-600" style={{ textTransform: 'none' }}>
                        Same term as today (no prepaid length change).
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500" style={{ textTransform: 'none' }}>
                    Change the package and/or prepaid term above to see the outcome here.
                  </p>
                )}
              </div>
            </section>

            <section className="pt-1 border-t border-gray-100">
              <h3
                className="text-sm font-semibold text-gray-900 mb-3"
                style={{ textTransform: 'none' }}
              >
                Payment
              </h3>
              <p className="text-xs text-gray-500 -mt-2 mb-3" style={{ textTransform: 'none' }}>
                Amount and reason.
              </p>
              <div className="space-y-3.5">
                <div>
                  <label
                    className={fieldLabel}
                    style={{ textTransform: 'none' }}
                    htmlFor="fcs-amount"
                  >
                    Custom amount (USD) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm"
                      aria-hidden
                    >
                      $
                    </span>
                    <input
                      id="fcs-amount"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className={`${inputClass} pl-7 tabular-nums`}
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500" style={{ textTransform: 'none' }}>
                    Agreed balance or quote, e.g. 487.00
                  </p>
                </div>
                <div>
                  <label
                    className={fieldLabel}
                    style={{ textTransform: 'none' }}
                    htmlFor="fcs-reason"
                  >
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="fcs-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. PROD-4503: Monthly LTO → 3M upgrade"
                    rows={2}
                    className={`${inputClass} resize-none`}
                  />
                </div>
                {/*
                <div>
                  <label
                    className={fieldLabel}
                    style={{ textTransform: 'none' }}
                    htmlFor="fcs-sub-start"
                  >
                    Subscription start <span className="font-normal text-gray-500">(optional)</span>
                  </label>
                  <input
                    id="fcs-sub-start"
                    type="date"
                    value={subscriptionStartDate}
                    onChange={(e) => setSubscriptionStartDate(e.target.value)}
                    className={`${inputClass} max-w-[11.5rem]`}
                    aria-label="Optional subscription start date"
                  />
                  <p className="mt-1.5 text-xs text-gray-500" style={{ textTransform: 'none' }}>
                    {`Leave empty to use today in the student's timezone. A date is midnight local, then stored in UTC.`}
                  </p>
                </div>
                */}
              </div>
            </section>

            <details className="group rounded-lg border border-dashed border-gray-200 bg-gray-50/50 px-3 py-2">
              <summary className="text-sm font-medium text-gray-600 cursor-pointer list-none flex items-center gap-2 hover:text-gray-800 [&::-webkit-details-marker]:hidden">
                <span className="text-gray-400 text-xs group-open:rotate-90 transition-transform">
                  ▶
                </span>
                Advanced options
              </summary>
              <div className="mt-3 pl-0.5">
                <label className="flex items-center gap-2.5 cursor-pointer" htmlFor="fcs-pkg-price">
                  <input
                    id="fcs-pkg-price"
                    type="checkbox"
                    checked={updatePackagePrice}
                    onChange={(e) => setUpdatePackagePrice(e.target.checked)}
                    className="h-4 w-4 accent-emerald-600"
                  />
                  <span className="text-sm text-gray-600" style={{ textTransform: 'none' }}>
                    Set{' '}
                    <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">package_price</code>{' '}
                    to the custom amount (default: standard list rate)
                  </span>
                </label>
              </div>
            </details>
          </div>

          {error && (
            <div
              className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="mt-6 flex gap-2 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-none rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePreview}
              disabled={fetchingDiscount}
              className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm ${fetchingDiscount ? 'bg-emerald-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
            >
              {fetchingDiscount ? 'Loading comparison…' : 'Continue to review'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="mb-5">
            <button
              type="button"
              onClick={() => {
                setStep(1);
                setError('');
              }}
              className="text-sm font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
            >
              ← Back to details
            </button>
            <h2
              className="text-lg font-semibold text-gray-900 mt-2"
              style={{ textTransform: 'none' }}
            >
              Review charge
            </h2>
            <p className="text-sm text-gray-500 mt-0.5" style={{ textTransform: 'none' }}>
              Confirm the amount and that the plan change matches what you agreed with ops.
            </p>
          </div>

          <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-3" style={{ textTransform: 'none' }}>
              What you are about to charge
            </p>
            <div className="space-y-3.5 text-sm">
              <div className={reviewRow}>
                <div className={reviewDt} style={{ textTransform: 'none' }}>
                  Student
                </div>
                <div className={reviewVal}>{studentName || '—'}</div>
              </div>
              <div className="h-px bg-gray-100" />
              <div className={reviewRow}>
                <div className={reviewDt} style={{ textTransform: 'none' }}>
                  Now
                </div>
                <div
                  className="text-sm min-w-0 sm:text-right text-gray-800 font-medium [word-break:break-word] leading-snug"
                  style={{ textTransform: 'none' }}
                >
                  {currentPlanLine}
                </div>
              </div>
              {showNewPackagePlanCard ? (
                <>
                  <div className="h-px bg-gray-100" />
                  <div className={reviewRow}>
                    <div className={reviewDt} style={{ textTransform: 'none' }}>
                      After charge
                    </div>
                    <div className="min-w-0 sm:text-right">
                      <p
                        className="text-gray-900 font-semibold [word-break:break-word] leading-snug"
                        style={{ textTransform: 'none' }}
                      >
                        {newPackagePrimaryLine || '—'}
                      </p>
                      {selectedWeeksLabel ? (
                        <p
                          className="mt-1 text-gray-600 text-sm font-normal"
                          style={{ textTransform: 'none' }}
                        >
                          {selectedWeeksLabel}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </>
              ) : null}
              {/*
              {subscriptionStartDate.trim() ? (
                <>
                  <div className="h-px bg-gray-100" />
                  <div className={reviewRow}>
                    <div className={reviewDt} style={{ textTransform: 'none' }}>
                      Billing start
                    </div>
                    <div className="min-w-0 sm:text-right font-mono text-sm text-gray-800">
                      {subscriptionStartDate.trim()}
                    </div>
                  </div>
                </>
              ) : null}
              */}
              {cycles !== null && (
                <>
                  <div className="h-px bg-gray-100" />
                  <div className={reviewRow}>
                    <div
                      className="text-sm text-gray-500 mb-0.5 sm:mb-0 sm:pt-0.5 sm:max-w-[9rem] leading-tight"
                      style={{ textTransform: 'none' }}
                    >
                      Prepaid cycles (est.)
                    </div>
                    <div className="min-w-0 sm:text-right tabular-nums font-medium text-gray-900">
                      {cycles}
                      {newNoOfWeeks ? (
                        <span
                          className="block sm:inline sm:ml-2 text-xs text-gray-500 font-normal"
                          style={{ textTransform: 'none' }}
                        >
                          {carryoverApplies ? 'with carryover' : 'default rule'}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
              <div className="h-px bg-gray-100" />
              <div className={reviewRow}>
                <div
                  className="text-sm text-gray-500 mb-0.5 sm:mb-0 sm:pt-0.5 self-start"
                  style={{ textTransform: 'none' }}
                >
                  Reason
                </div>
                <div
                  className="text-gray-700 min-w-0 sm:text-right leading-relaxed [word-break:break-word] font-normal"
                  style={{ textTransform: 'none' }}
                >
                  {reason}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm text-sm">
            {standardPrice && (
              <div
                className="grid grid-cols-[1fr_auto] items-baseline gap-3 border-b border-gray-100 bg-slate-50/80 px-4 py-2.5"
                style={{ textTransform: 'none' }}
              >
                <span className="text-gray-500">Reference list price (from system)</span>
                <span className="text-gray-400 line-through tabular-nums text-right">
                  {standardPrice}
                </span>
              </div>
            )}
            <div className="grid grid-cols-[1fr_auto] items-baseline gap-3 px-4 py-4">
              <div style={{ textTransform: 'none' }}>
                <p className="font-medium text-gray-800">Custom charge (card)</p>
                <p className="text-xs text-gray-500 mt-0.5">This is the amount to collect now.</p>
              </div>
              <span className="text-2xl font-bold text-gray-900 tabular-nums sm:text-right">
                ${Number(amount).toFixed(2)}
              </span>
            </div>
          </div>

          <p
            className="mb-4 rounded-lg border border-amber-200/80 bg-amber-50/90 px-3.5 py-2.5 text-sm text-amber-950"
            role="status"
            style={{ textTransform: 'none' }}
          >
            <span className="font-medium text-amber-900">Note: </span>
            The card is charged immediately. This action is not automatically reversible from here.
          </p>

          {error && (
            <div
              className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="flex gap-2 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-none rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm ${loading ? 'bg-emerald-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
            >
              {loading ? 'Charging…' : 'Confirm and charge card'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default FlexibleChargeSubscription;
